import * as React from 'react';
import { Provider, useSelector } from 'react-redux';

import type { AiCoach_Action as AiCoachAction, AiCoach_Mode as AiCoachMode } from '__generated__/graphql-types';

import type { ButtonProps } from '@coursera/cds-core';
import type { CourseItem, Product } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachTriggerButton } from 'bundles/ai-coach-platform/components/building-blocks';
import store from 'bundles/ai-coach-platform/components/patterns/chat/store';
import { openCoachWidget, sendMessage } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { GlobalState } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar/useCourseToolBarPlugins';

type ExternalCoachActionButtonProps = {
  mode: AiCoachMode;
  action: AiCoachAction;
  onClick?: (e?: React.MouseEvent) => void;
  label: string;
  location: 'chat' | 'disclosure' | 'toolbar';
  courseId?: string;
  specializationId?: string;
  itemId?: string;
} & Omit<ButtonProps, 'children'>;

type StoreProviderWrapperProps = {
  children: JSX.Element;
};

const StoreProviderWrapper = ({ children }: StoreProviderWrapperProps): React.ReactElement => {
  return <Provider store={store}>{children}</Provider>;
};

const ExternalCoachActionButtonContent = (props: ExternalCoachActionButtonProps): React.ReactElement => {
  const { mode, action, onClick, label, location, courseId, itemId, specializationId } = props;

  const { setToolSelected, setExpanded } = useCourseToolBarPlugins();

  const { isMessageLoading } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
  }));

  const track = useTracker();

  const sharedEventingData = {
    courseItem: {
      id: itemId as string,
    } as CourseItem,
    product: {
      type: 'course',
      id: courseId as string,
    } as Product,
  };

  return (
    <CoachTriggerButton
      disabled={isMessageLoading}
      onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
          onClick(e);
        }

        setToolSelected('coach');
        setExpanded(true);
        openCoachWidget();
        sendMessage({
          input: {
            message: label,
            courseraContext: {
              courseId,
              itemId,
              specializationId,
            },
            config: {
              coachActionId: action,
              coachModeId: mode,
            },
          },
        });

        // v3 tracking
        track('interact_coach', {
          coachInteractionType: 'click_action',
          coach: {
            mode,
            location: location,
          },
          ...(courseId && { product: sharedEventingData.product }),
          ...(itemId && { courseItem: sharedEventingData.courseItem }),
          coachAction: action,
        });
      }}
    >
      {label}
    </CoachTriggerButton>
  );
};

export const ExternalCoachActionButton = (props: ExternalCoachActionButtonProps): React.ReactElement => {
  return (
    <StoreProviderWrapper>
      <ExternalCoachActionButtonContent {...props} />
    </StoreProviderWrapper>
  );
};

export default ExternalCoachActionButton;
